import { useState } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import { providerActions } from '../../../../store';
import { ClinicalRelationships } from '../../../../constants';
import { ProviderClinicalStatus } from '../../../constants/general';
import {
  useCanInviteProviderToPlatform,
  useInviteProviderToPlatform,
} from '../../../hooks';
import { useProviderDetailsState } from './useProviderDetailsState';

import type { AppDispatch } from '../../../../store';
import type { IProviderAssignment } from '../../../../utils/types';

// Owns the "Invite to platform" eligibility and action for the provider detail header.
export function useProviderInviteToPlatform() {
  const dispatch = useDispatch<AppDispatch>();
  const [isInviting, setIsInviting] = useState(false);
  const { provider, clientAssignments, clientAssignmentsLoading } = useProviderDetailsState();
  const canInviteToPlatform = useCanInviteProviderToPlatform();
  const inviteProviderToPlatform = useInviteProviderToPlatform(provider?.id);

  // The invite targets a parent, so gate on an active practicing-guardian assignment.
  // Read the unfiltered Redux assignments and fail closed while they load. Match the
  // current provider id, so a stale list from another provider (for example after a
  // failed fetch) cannot show the action. Require a started, not-ended assignment,
  // matching the backend's active check (a missing start is not active).
  const currentDay = moment();
  const hasActiveGuardianAssignment =
    !clientAssignmentsLoading &&
    clientAssignments.some(
      (assignment: IProviderAssignment) =>
        assignment.provider?.id === provider?.id &&
        assignment.clinical_relationship === ClinicalRelationships.PracticingGuardian &&
        Boolean(assignment.date_start) &&
        moment(assignment.date_start).isSameOrBefore(currentDay, 'day') &&
        (!assignment.date_end || moment(assignment.date_end).isSameOrAfter(currentDay, 'day')),
    );

  // The backend re-checks the pre-clinical status and the active assignment before it acts.
  const showInviteToPlatform =
    canInviteToPlatform &&
    provider?.clinical_status === ProviderClinicalStatus.PRE_CLINICAL &&
    hasActiveGuardianAssignment;

  const handleInviteToPlatform = async () => {
    // The invite is not idempotent from the UI's side, so block re-entry: a second
    // click before the first request resolves would fire a duplicate PUT.
    if (isInviting) {
      return;
    }
    setIsInviting(true);
    try {
      const res = await inviteProviderToPlatform();
      // On success, update the shared provider lists so the new status also shows on
      // the admin dashboard. The invite hook already re-fetches the detail view.
      if (!res?.error && res?.payload) {
        dispatch(providerActions.updateProviderLists(res.payload));
      }
    } finally {
      setIsInviting(false);
    }
  };

  return { showInviteToPlatform, isInviting, handleInviteToPlatform };
}
